import { type FC, useEffect, useState } from "react";
import { useRef } from "react";
import type { RJSFSchema, FieldProps } from "@rjsf/utils";
import { getSchemaType, mergeObjects } from "@rjsf/utils";
import { Alert, styled, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import MediaSelectorField from "@/framework/KioForm/fields/MediaSelectorField/MediaSelectorField";

import { deepEqual } from "fast-equals";

import ImageMapLeafletLoader from "@/components/Leaflet/ImageMapLeafletImageLoader";

const ImageMapContainer = styled("div")`
  margin-top: 30px;
  padding-bottom: 30px;
`;

const ImageMapLeafletField: FC<FieldProps> = ({
  schema,
  uiSchema,
  formContext,
  formData,
  onChange,
  required,
  disabled,
  readonly,
  idSchema,
  errorSchema,
  onBlur,
  onFocus,
  registry,
  autofocus,
}) => {
  const { t } = useTranslation("common");
  const { schemaUtils } = registry;

  // HACK: same hack/workaround as in DocumentRelationField.
  // When the SchemaFields onChange is called, formData somehow contains the default value
  // for background, removing selected mediaId.
  const backgroundImageRef = useRef<any>(formData?.["background"]);

  const _schema = schemaUtils.retrieveSchema(schema, formData);

  const backgroundImageFormData = { ...formData?.["background"] };
  const markersFormData: any[] = formData?.["markers"] || [];
  const [markers, setMarkers] = useState(markersFormData.map((elem) => ({ ...elem })));

  const backgroundImageSchema = _schema.properties?.background
    ? schemaUtils.retrieveSchema(_schema.properties?.background as RJSFSchema, backgroundImageFormData)
    : null;
  const markersSchema = _schema.properties?.markers
    ? schemaUtils.retrieveSchema(_schema.properties?.markers as RJSFSchema, markersFormData)
    : null;

  const errorPrefix = "kioForm.fields.ImageMapField.errors";

  function* schemaErrors(): IterableIterator<string> {
    if (backgroundImageSchema == null) {
      yield `${errorPrefix}.missingBackgroundSchema`;
    }
    if (markersSchema == null) {
      yield `${errorPrefix}.missingMarkersSchema`;
    } else {
      if (markersSchema.type !== "array") {
        yield `${errorPrefix}.notArray`;
      }
      const resolvedItemSchema = schemaUtils.retrieveSchema(markersSchema.items as RJSFSchema, markersFormData);
      if (getSchemaType(resolvedItemSchema) !== "object") {
        yield `${errorPrefix}.itemsNotObject`;
      }
      if (!resolvedItemSchema.properties?.["coordinates"]) {
        yield `${errorPrefix}.noCoordinates`;
      }
    }
  }

  const errors = Array.from(schemaErrors());
  if (errors.length) {
    return (
      <>
        {errors.map((e) => (
          <Typography key={e} color="error">
            {t(e)}
          </Typography>
        ))}
      </>
    );
  }

  const defaultMarkersUISchema = {
    items: {
      coordinates: {
        x: {
          "ui:widget": "range",
        },
        y: {
          "ui:widget": "range",
        },
      },
    },
  };
  const markersUISchema = mergeObjects(defaultMarkersUISchema, uiSchema?.["markers"]);

  const handleImageChange = (imageFormData: any) => {
    // See above "HACK:"
    backgroundImageRef.current = imageFormData;
    onChange({
      ...formData,
      background: imageFormData,
    });
  };

  function onMarkersUpdate(markers: any[]) {
    onChange({
      ...formData,
      // see above "HACK:"
      background: backgroundImageRef.current,
      markers,
    });
  }

  useEffect(() => {
    if (markers && !deepEqual(formData.markers, markers)) {
      onChange({
        ...formData,
        markers: markers.map((markerElems) => ({ ...markerElems })),
      });
    }
  }, [markers]);

  useEffect(() => {
    const synchronizeMarkersAndFormDataMarkers = () => {
      setMarkers(
        formData.markers.map((formDataElem: any) => {
          return {
            ...formDataElem,
            coordinates: {
              ...(markers.find(
                (mElem) =>
                  (mElem.document_id &&
                    formDataElem.document_id && // This prevents undefined===undefined
                    mElem?.document_id === formDataElem?.document_id) ||
                  (mElem.to_document_id &&
                    formDataElem.to_document_id && // This prevents undefined===undefined
                    mElem?.to_document_id === formDataElem?.to_document_id)
              )?.coordinates || { x: 50, y: 50 }),
            },
          };
        })
      );
    };

    if (markers.length != formData.markers.length) {
      synchronizeMarkersAndFormDataMarkers();
    } else if (
      /*
       * Handles if the order of elements changes. We need to check for this,
       * because we have no direct control over components behind SchemaField.
       * The component supports both DocumentRelationField and StateRelationPickerField.
       * So we need to handle both document_id or to_document_id.
       */
      !formData.markers.every((formDataMarkerElem: any, idx: number) => {
        return (
          (formDataMarkerElem.document_id &&
            markers[idx].document_id && // This prevents undefined===undefined
            formDataMarkerElem?.document_id === markers[idx]?.document_id) ||
          (formDataMarkerElem.to_document_id &&
            markers[idx].to_document_id && // This prevents undefined===undefined
            formDataMarkerElem?.to_document_id === markers[idx]?.to_document_id)
        );
      })
    ) {
      synchronizeMarkersAndFormDataMarkers();
    }
  }, [formData.markers]);

  const { SchemaField } = registry.fields;

  return (
    <>
      <MediaSelectorField
        schema={backgroundImageSchema as RJSFSchema}
        uiSchema={uiSchema?.["background"]}
        /* @RJSF5-ERR */
        idSchema={idSchema["background"]}
        formData={formData?.["background"]}
        errorSchema={errorSchema?.["background"]}
        onChange={handleImageChange}
        onBlur={onBlur}
        onFocus={onFocus}
        registry={registry}
        formContext={formContext}
        autofocus={autofocus}
        disabled={disabled}
        readonly={readonly}
        required={required}
        name={`background`}
      />
      <ImageMapContainer>
        {backgroundImageRef.current?.mediaId ? (
          <ImageMapLeafletLoader
            mediaId={backgroundImageRef.current?.mediaId}
            markers={markers}
            setMarkers={setMarkers}
          />
        ) : (
          <div></div>
        )}
      </ImageMapContainer>

      <SchemaField
        formData={markersFormData}
        schema={markersSchema as RJSFSchema}
        registry={registry}
        formContext={formContext}
        onChange={onMarkersUpdate}
        /* @RJSF5-ERR */
        idSchema={idSchema["markers"]}
        uiSchema={markersUISchema}
        errorSchema={errorSchema?.["markers"]}
        autofocus={autofocus}
        disabled={disabled}
        readonly={readonly}
        required={required}
        onBlur={onBlur}
        onFocus={onFocus}
        name={"markers"}
      />
    </>
  );
};

export default ImageMapLeafletField;
